import { useLazyQuery, useQuery } from "@apollo/client/react";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/Remove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Grid } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DocumentViewer from '../../../../components/DocumentViewer';
import { CustomButton } from '../../../../components/customButton/CustomButton';
import toast from 'react-hot-toast';
import DataGridGenerico from '../../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../../components/dataGrid/columnHelpers';
import { PopoverModal } from '../../../../components/popoverModal/PopoverModal';
import GET_ALL_ORDEN_PEDIDOS from '../../../../data/graphQL/queries/GestionAsistencial/getAllOrdenPedidos';
import GET_ARCHIVO_REMITO from '../../../../data/graphQL/queries/GestionAsistencial/getArchivoRemito';
import { VALIDAR_CREAR_PEDIDO } from "../../../../data/graphQL/mutations/GestionAsistencial/validarCrearPedido";

export const TabEnvios = ({ 
  idEvaluacionSelected, 
  allowedWrite, 
  evaluacionSelected,
  cambioDireccion  
}) => {
  const location = useLocation();
  const datosDetalle = location.state;

  const enviosLoading = false;
  const [rowsEnvios, setRowsEnvios] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const navigate = useNavigate();

  const { data: ordenPedidos, refetch, loading } = useQuery(GET_ALL_ORDEN_PEDIDOS, {
    fetchPolicy: 'no-cache',
    variables: {
      idEvaluacion: idEvaluacionSelected
    },
    skip: !idEvaluacionSelected
  });

  useEffect(() => {
    if (!ordenPedidos) return;
    setRowsEnvios(ordenPedidos?.allOrdenPedidos);
  }, [ordenPedidos]);

  const [validarCrearPedido] = useLazyQuery(VALIDAR_CREAR_PEDIDO, {
    fetchPolicy: 'no-cache'
  });

  const [getArchivoRemito, { data: dataArchivoRemito }] = useLazyQuery(GET_ARCHIVO_REMITO, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!dataArchivoRemito) return;
    if (dataArchivoRemito?.archivoRemito?.success)
      setSelectedFile(dataArchivoRemito.archivoRemito.valorEsperado?.at());
  }, [dataArchivoRemito]);

  const totalCount = useMemo(() => rowsEnvios?.length || 0, [rowsEnvios]);

  const configuratedColumns = useGetEnviosColumns(getArchivoRemito, idEvaluacionSelected, datosDetalle, allowedWrite);

  useEffect(() => { refetch(); }, [idEvaluacionSelected]);

  const enviosPath = "/envio/nuevoEnvio";    

  const handleNuevo = async () => {
    if (cambioDireccion) {
      toast.error("El paciente no se encuentra correctamente geoposicionado, por favor dirigirse a la pestaña Ubicaciones para actualizar la información.");
    } else {
      await validarCrearPedido({
      variables: {
        idEvaluacion: idEvaluacionSelected
      }
    }).
      then((data) => {
        if (!data?.data?.validarCrearPedido?.valorEsperado) {
          toast.error(data?.data?.validarCrearPedido?.error);
          return;
        }

        navigate(enviosPath, {
          state: {
            datosDetalle: {
              ...datosDetalle,
              fromScreen: false,
              allowedWrite: true
            },
            isNew: true,
            idEvaluacion: idEvaluacionSelected,
            idOrdenPedido: 0
          }
        });
      }).catch(() => toast.error('Ha ocurrido un error al validar el pedido'));
    }    
  };

  const onClickActualizar = () => {
    refetch();
  };

  const handleRowClick = () => { };

  const closeViewer = () => { setSelectedFile(null); };

  return (
    <div className='generic-tab-container'>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <div className='generic-header-tab-content-with-buttons'>
            <strong>Envíos</strong>
            <div className='button-save-geo-data'>
              <CustomButton
                onClick={onClickActualizar}
                label='Actualizar'
                loading={loading}
                disabled={!allowedWrite}
                variant='outlined' />

              <CustomButton
                sx={{ marginLeft: '20px' }}
                onClick={handleNuevo}
                label='Nuevo'
                disabled={!allowedWrite || evaluacionSelected?.tieneSuspensionObito}
                variant='contained' />

            </div>
          </div>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <DataGridGenerico
            hideFooterPagination={false}
            loading={enviosLoading}
            toolbar={false}
            columns={configuratedColumns}
            rows={rowsEnvios}
            totalCount={totalCount}
            onRowClick={handleRowClick}
            initialState={{
              sorting: {
                sortModel: [{ field: 'idOrdenPedido', sort: 'desc' }]
              }
            }} />

        </Grid>
      </Grid>
      {!!selectedFile?.titulo && <DocumentViewer data={selectedFile} setOpenDlg={closeViewer} />}
    </div>);

};


const useGetEnviosColumns = (getArchivoRemito, idEvaluacionSelected, datosDetalle, allowedWrite, evaluacionSelected) => {
  const navigate = useNavigate();
  const asistencialPath = "/asistencial/gestionAsistencial";
  const enviosPath = "/envio/nuevoEnvio";
  const editableEstados = ['Pendiente', 'A Autorizar'];

  const handleClickEdit = (row) => {
    navigate(enviosPath, {
      state: {
        datosDetalle: {
          ...datosDetalle,
          fromScreen: false,
          allowedWrite: true,
          tieneSuspensionObito: true
        },
        isNew: false,
        idEvaluacion: idEvaluacionSelected,
        idOrdenPedido: row.idOrdenPedido
      }
    });
  };
  const handleClickDuplicate = (row) => {
    navigate(asistencialPath, {
    });
  };

  const handleClickView = (row) => {
    navigate(enviosPath, {
      state: {
        datosDetalle: {
          ...datosDetalle,
          fromScreen: false,
          allowedWrite: false
        },
        isNew: false,
        idEvaluacion: idEvaluacionSelected,
        idOrdenPedido: row.idOrdenPedido
      }
    });
  };

  const showArchivoRemito = (row) => {
    getArchivoRemito({
      variables: {
        remito: row?.numeroRemito
      }
    });
  };

  const initialColumns = [
    {
      headerName: '',
      field: 'action',
      flex: 40,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return (
          <PopoverModal>
            <div className="popover-modal-content">
              <div onClick={() => handleClickView(row)} aria-hidden='true'
                className='item-option clickable-option'>

                <VisibilityIcon className='edit-icon' />
                <span className='toggle-action'>Ver</span>
              </div>
              {allowedWrite && editableEstados.includes(row.estadoDescripcion) && <div onClick={() => handleClickEdit(row)} aria-hidden='true'
                className='item-option clickable-option'>

                <EditIcon className='edit-icon' />
                <span className='toggle-action'>Editar</span>
              </div>}
              {/* {row.estadoDescripcion.includes('Remitido') && <div onClick={() => handleClickDuplicate(row)} aria-hidden='true'
                 className='item-option clickable-option'
              >
                 <ContentCopyIcon className='edit-icon' />
                 <span className='toggle-action'>Duplicar</span>
              </div>} */}
            </div>
          </PopoverModal>);

      }
    },
    {
      flex: 90,
      field: "idOrdenPedido",
      headerName: "#"
    },
    {
      flex: 90,
      field: "codSAPVenta",
      headerName: "Código SAP"
    },
    {
      flex: 150,
      field: "fechaEntrega",
      headerName: "F. de entrega",
      type: 'date'
    },
    {
      flex: 150,
      field: "numeroRemito",
      headerName: "Remito"
    },
    {
      flex: 150,
      field: "estadoDescripcion",
      headerName: "Estado Descripción"
    },
    {
      flex: 150,
      field: "tieneArchivoRemito",
      headerAlign: 'center',
      align: 'center',
      headerName: "Remito",
      renderCell: ({ row }) => {
        if (!row?.tieneArchivoRemito) return <RemoveIcon className='edit-icon' sx={{ mr: 0 }} />;
        return <AttachFileIcon
          className='edit-icon'
          sx={{ mr: 0 }}
          onClick={() => showArchivoRemito(row)} />;

      }
    }];


  const columns = useMemo(
    () => generateColumnsDefaultConfiguration(initialColumns),
    [initialColumns]
  );

  return columns;
};